/* eslint-disable import/no-named-as-default */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { object, oneOf, shape, string } from 'prop-types';
import get from 'lodash/get';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import { getIrisTemplateData } from 'yoda-site-components/lib/actions/IrisAction';
import { getCookie } from 'yoda-interfaces/lib/helpers/Cookies';
import IrisPage from 'yoda-site-components/lib/components/IrisPage/IrisPage';
import Layout from 'yoda-site-components/lib/components/Layout/Layout';
import ScrollTop from 'yoda-site-components/lib/components/ScrollTop/ScrollTop';
import isEmpty from 'lodash/isEmpty';
import irisComponentsForGallery from 'yoda-site-components/lib/iris/components/forGallery';
import {
    selectShouldRender,
    selectIsNative,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import MiniPDPInterstitialAsync from 'yoda-mini-pdp/lib/components/MiniPDPInterstitial/MiniPDPInterstitialAsync';
import isBloomReachAudience, {
    enableBloomReachPage,
} from 'yoda-site-components/lib/helpers/BloomReach/BloomReach';

import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import RestClient from 'yoda-interfaces/lib/RestClient/RestClient';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { findMobileOS } from 'yoda-core-components/lib/helpers/Utils/Utils';
import { showLoader } from '../../../actions/LoaderActions';
import GalleryLoader from '../../../components/GalleryLoader/GalleryLoader';

import { getGalleryDetailsById, initiatePendingActions } from '../../../actions/GalleryAction';
import {
    getGalleryContentWithIris,
    setInitialIrisRequestMode,
} from '../../../actions/GalleryIrisAction';

import { patterns, catIdRedirect } from '../../../configurations/config';
import {
    galleryPostProcessorForIris,
    getIrisParamForGallery,
} from '../../../helpers/IrisApiCallingFunctions';
import redirectRequired from '../../../helpers/RedirectHelper';

import { getIrisFallBackStatus } from '../../../selectors/IrisFailure';
import {
    addToRecentSearch,
    compareUrls,
    isContentHydratedWithIris,
    isFirstPageWithoutPrev,
    getURLParameterBykey,
    isHydrated,
    updateUrl,
    getDeviceType,
    getContentDate,
} from '../../../utils';
import SeoSchema from '../../../components/SeoSchema/SeoSchema';
import StoreAnalytics from '../../../components/StoreAnalytics/StoreAnalytics';
import TimeoutError from '../../../components/TimeoutError/TimeoutError';
import FailOverLayout from '../../../components/FailOverLayout/FailOverLayout';
import gridZoneRender from '../../../iris/zoneOverrides/grid';
import aboveTheGridZoneOverride from '../../../iris/zoneOverrides/aboveTheGrid';
import irisPageSpecificComponents from '../../../iris/components';
import { fail } from './constants';

/**
 * IRIS integration wiki: https://wiki.jcpenney.com/pages/viewpage.action?pageId=79664545
 * Please update if logic changes are made in this file.
 */
/* istanbul ignore file */

// Track if SSR initialization has happened
let ssrInitialized = false;

export const IrisWrapper = ({ location, pageType, cookies: { DPCPT = '' } = {} }) => {
    const dispatch = useDispatch();
    const [mounted, setMounted] = useState(false);
    const initRef = useRef(false);
    const locationHrefRef = useRef(null);
    const historyUnlistenRef = useRef(null);

    // Combine selectors to reduce re-renders
    const stateData = useSelector((state) => {
        const context = state?.context || {};
        const featureFlags = context?.featureFlags || {};
        const preferences = context?.preferences || {};
        const deviceType = context?.deviceType || {};

        return {
            refreshContent: state?.refreshContent,
            hostname: get(context, 'hostname', ''),
            seoConfig: get(preferences, 'seoConfig', {}),
            userSegAB: get(preferences, 'userSegAB', ''),
            plpBloomreachInclusionList: preferences?.plpBloomreachInclusionList,
            requestUrl: get(context, 'requestUrl', ''),
            queryFromContext: get(context, 'query', ''),
            isBot: deviceType?.isBot || false,
            commonData: state?.commonData || {},
            irisData: state?.irisData,
            irisCallStatus: state?.irisContent?.irisCallStatus,
            irisContentCallCompleted: state?.irisContent?.contentCallCompleted,
            isNative: selectIsNative(state),
            irisFallBackStatus: getIrisFallBackStatus(state),
            shouldRender: selectShouldRender(state),

            // Feature flags
            enableSmoothScrollIOSGallerySearch: get(
                featureFlags,
                'enableSmoothScrollIOSGallerySearch',
                false
            ),
            enableMultiSelectFilters: get(featureFlags, 'enableMultiSelectFilters', false),
            showBackToTop: get(featureFlags, 'showBackToTop', false),
            enableCanonicalFallback: get(featureFlags, 'enableCanonicalFallback', true),
            enableABTest: get(featureFlags, 'enableABTest', false),
            disableIrisCall: get(featureFlags, 'disableIrisCall', false),
            enableSSRForPLP: get(featureFlags, 'enableSSRForPLP', false),
            enableV1Redirect: get(featureFlags, 'enableV1Redirect', true),
            isClientSideRenderingEnabled: get(featureFlags, 'enableRealTimeInventory', true),
            enableDefaultFilterForPreview: get(
                featureFlags,
                'enableDefaultFilterForPreview',
                false
            ),
            enableBloomReach: get(featureFlags, 'enableBloomReach', false),
            enableBloomReachV3PLP: get(featureFlags, 'enableBloomReachV3PLP', true),
            enableBloomReachNative: get(featureFlags, 'enableBloomReachNative', false),
            enableBrAbTestAlgo: get(featureFlags, 'enableBrAbTestAlgo', true),
            enableMiniPDPFeature: get(featureFlags, 'enableMiniPDPFeature', false),
            enableQueryParmsFmContext: get(featureFlags, 'enableQueryParmsFmContext', false),
        };
    }, shallowEqual);

    const {
        refreshContent,
        hostname,
        seoConfig,
        userSegAB,
        enableSmoothScrollIOSGallerySearch,
        plpBloomreachInclusionList,
        requestUrl,
        isNative,
        irisFallBackStatus,
        shouldRender,
        isBot,
        commonData,
        irisData,
        irisCallStatus,
        irisContentCallCompleted,
        queryFromContext,
        enableMultiSelectFilters,
        showBackToTop,
        enableCanonicalFallback,
        enableABTest,
        disableIrisCall,
        enableSSRForPLP,
        enableV1Redirect,
        isClientSideRenderingEnabled,
        enableDefaultFilterForPreview,
        enableBloomReach,
        enableBloomReachV3PLP,
        enableBloomReachNative,
        enableBrAbTestAlgo,
        enableMiniPDPFeature,
        enableQueryParmsFmContext,
    } = stateData;

    // Memoize postProcessor
    const postProcessor = useMemo(
        () =>
            galleryPostProcessorForIris.bind({
                getGalleryContentWithIris: getGalleryContentWithIris(),
            }),
        []
    );

    const loadIrisGalleryContent = useCallback(
        (loc, initialLoad) => {
            if (!disableIrisCall) {
                if (!__SERVER__ && initialLoad) {
                    dispatch(setInitialIrisRequestMode());
                }
                const params = getIrisParamForGallery(loc);
                const aggregateAPI = true;
                dispatch(
                    getIrisTemplateData(
                        params,
                        aggregateAPI,
                        postProcessor,
                        undefined,
                        undefined,
                        undefined,
                        false,
                        false,
                        initialLoad,
                        false
                    )
                );
            }
        },
        [disableIrisCall, dispatch, postProcessor]
    );

    const getAbTestParams = useCallback(() => {
        let payload = [];
        if (!__SERVER__ && enableABTest && pageType === 'Gallery') {
            const pageURL = get(window.location, 'href', '');
            const referrerURL = get(window.location, 'origin', '');
            payload = [
                {
                    pageURL,
                    referrerURL,
                },
            ];
            return payload;
        }
        return {};
    }, [enableABTest, pageType]);

    const getPreviewParams = useCallback(
        (initialLoad, loc = {}, context = {}) => {
            let params = {};
            const urlStatus = getURLParameterBykey('item_status', loc.search);
            const facetId = getURLParameterBykey('activeFacetId', loc.search);
            if (!__SERVER__ && context.isPreview) {
                params = {
                    channel: getDeviceType((context && context.deviceType) || {}),
                    contentDate: getContentDate(),
                };
            }

            if (
                initialLoad &&
                !urlStatus &&
                context.isPreview &&
                enableDefaultFilterForPreview &&
                facetId !== '44710'
            ) {
                params = { ...params, item_status: 1 };
            }
            return params;
        },
        [enableDefaultFilterForPreview]
    );

    const loadGalleryDetails = useCallback(
        (loc, initialLoad, currentIsNative, isBloomReachV3Page = false) => {
            if (loc) {
                const abTestParams = getAbTestParams();

                const updatedSearch = () => {
                    let newSearch = loc.search;
                    const isSearchPage = pageType === 'Search';
                    const isNativeBloomReach = enableBloomReachNative ? false : currentIsNative;
                    const bloomReachAudience = isBloomReachAudience(
                        enableBloomReach,
                        isNativeBloomReach
                    );

                    if (
                        userSegAB &&
                        !getURLParameterBykey('UsrSeg', loc.search) &&
                        !isBloomReachV3Page
                    ) {
                        const useSegABValue = getCookie(userSegAB);
                        if (useSegABValue) newSearch = newSearch.concat(`&${useSegABValue}`);
                    }

                    if (
                        bloomReachAudience &&
                        isSearchPage &&
                        !getURLParameterBykey('UsrSeg', loc.search)
                    ) {
                        const brTestAlgo = enableBrAbTestAlgo
                            ? Cookies?.load('BRTestAlgo', true)
                            : null;
                        newSearch = newSearch.concat(`&UsrSeg=${Cookies?.load('BRtest', true)}`);
                        if (brTestAlgo?.length > 0) newSearch = newSearch.concat(`&${brTestAlgo}`);
                    }
                    if (isBloomReachV3Page) {
                        const brTestAlgo =
                            enableBrAbTestAlgo && isSearchPage
                                ? Cookies?.load('BRTestAlgo', true)
                                : null;
                        if (brTestAlgo?.length > 0) newSearch = newSearch.concat(`&${brTestAlgo}`);
                    }
                    return newSearch;
                };

                if (!__SERVER__ && enableSmoothScrollIOSGallerySearch && findMobileOS() === 'iOS') {
                    if (document.documentElement.classList.contains('scroll-smooth')) {
                        document.documentElement.classList.remove('scroll-smooth');
                    }
                }

                const query = makeQuery(updatedSearch());
                const params = {
                    ...query,
                    ...getPreviewParams(initialLoad, loc, RestClient.getContext()),
                };
                let apiPath;

                if (query?.id && loc?.pathname?.match(catIdRedirect.pattern)) {
                    apiPath = catIdRedirect?.apiPath;
                    params.id = query.id;
                } else {
                    apiPath = loc.pathname.substr(1);
                }

                dispatch(
                    getGalleryDetailsById({
                        loc,
                        params,
                        apiPath,
                        initialLoad,
                        abTestParams,
                        pageType,
                    })
                );

                if (!__SERVER__ && enableSmoothScrollIOSGallerySearch && findMobileOS() === 'iOS') {
                    if (!document.documentElement.classList.contains('scroll-smooth')) {
                        setTimeout(() => {
                            document.documentElement.classList.add('scroll-smooth');
                        }, 1500);
                    }
                }
            }
        },
        [
            pageType,
            getAbTestParams,
            getPreviewParams,
            userSegAB,
            enableBloomReach,
            enableBloomReachNative,
            enableBrAbTestAlgo,
            enableSmoothScrollIOSGallerySearch,
            dispatch,
        ]
    );

    // Server-side initialization - runs synchronously during render
    if (__SERVER__ && !ssrInitialized && !mounted) {
        // ssrInitialized = true;

        const redirectStatus = redirectRequired(commonData, location?.pathname);
        let invokeDetailsFromClient = isClientSideRenderingEnabled && !isBot;
        const isSearchPage = pageType === 'Search';

        const isBloomReachV3Page = enableBloomReachPage(
            enableBloomReachV3PLP,
            requestUrl,
            plpBloomreachInclusionList
        );

        if (enableV1Redirect) {
            const v1patterns = patterns?.v1patterns || {};
            const gallery = v1patterns.gallery || {};
            if (gallery.pattern && gallery.flags) {
                const v1GalleryPattern = new RegExp(gallery.pattern, gallery.flags);
                invokeDetailsFromClient =
                    invokeDetailsFromClient && !v1GalleryPattern.test(location?.pathname);
            }
        }

        const ssrThrottleEnabledForPLP = DPCPT?.includes('CP4Y');

        const shouldLoadGalleryDetails = [
            !isBot,
            !isSearchPage,
            enableSSRForPLP,
            ssrThrottleEnabledForPLP,
        ].every((condition) => condition);

        if (!redirectStatus) {
            if (shouldLoadGalleryDetails || !invokeDetailsFromClient) {
                loadGalleryDetails(location, false, isNative, isBloomReachV3Page);
            }
            loadIrisGalleryContent(location, true);
        }
    }

    // Client-side initialization
    useEffect(() => {
        // Prevent double initialization
        if (initRef.current) return;
        initRef.current = true;

        if (__SERVER__) return;

        const redirectStatus = redirectRequired(commonData, location?.pathname);
        const invokeDetailsFromClient = isClientSideRenderingEnabled && !isBot;
        const isFirstPage = isFirstPageWithoutPrev();
        const marketingTilesCount = isFirstPage ? 0 : getURLParameterBykey('mktTiles');
        const isGalleryPage = pageType === 'Gallery';
        const isSearchPage = pageType === 'Search';

        const isBloomReachV3Page = enableBloomReachPage(
            enableBloomReachV3PLP,
            requestUrl,
            plpBloomreachInclusionList
        );

        window.jcpDLjcp = {};

        // Safari private browser logic
        if (enableQueryParmsFmContext && window?.location?.search === '') {
            let queryString = '?';
            if (!isEmpty(queryFromContext)) {
                Object.entries(queryFromContext).forEach(([key, value]) => {
                    queryString += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
                });
                window.location.search = queryString?.substring(0, queryString?.length - 1);
            }
        }

        locationHrefRef.current = window?.location?.href;
        const invokeGalleryDetails = !isHydrated(location);

        // When unable to render server-side (e.g. Server Too Busy) get all content zones.
        if (!isContentHydratedWithIris(irisData)) {
            loadIrisGalleryContent(location, true);
        }

        // Load gallery details if needed
        if (invokeGalleryDetails || (invokeDetailsFromClient && !enableSSRForPLP)) {
            loadGalleryDetails(location, true, isNative, isBloomReachV3Page);
        } else {
            dispatch(initiatePendingActions());
        }

        // Set up history listener
        const history = getBrowserHistory();
        const unlisten = history.listen((loc) => {
            const currentLocation = window.location.href;
            const isReplaceAction = history?.action === 'REPLACE';
            const isBackBtn = history?.action === 'POP';
            const isCurrentLocation = locationHrefRef.current === currentLocation;
            const isNoResultsPage = !!LocalStorage.getData('NoResultsPage');

            if (isBackBtn && !isNoResultsPage) {
                const browserBFButtonClick = LocalStorage.getData('browserBFButtonClick');
                LocalStorage.setData('browserBFButtonClick', browserBFButtonClick !== 'undoBtn');
                if (locationHrefRef.current && locationHrefRef.current.indexOf('page=') !== -1) {
                    LocalStorage.setData('paginationClickBack', true);
                }
                dispatch(showLoader());
            }

            if (!isReplaceAction && (!isCurrentLocation || refreshContent)) {
                locationHrefRef.current = currentLocation;
                loadIrisGalleryContent(loc, false);

                if (isGalleryPage || isSearchPage) {
                    loadGalleryDetails(loc, isNoResultsPage, isNative, isBloomReachV3Page);
                }
            }
        });

        historyUnlistenRef.current = unlisten;

        if (isSearchPage) {
            addToRecentSearch();
        }

        setMounted(true);
    }, []); // Empty deps ensures this runs only once

    // Cleanup
    useEffect(() => {
        return () => {
            if (historyUnlistenRef.current) {
                historyUnlistenRef.current();
            }
            // Reset for HMR
            if (!__SERVER__) {
                ssrInitialized = false;
                initRef.current = false;
            }
        };
    }, []);

    // Previous value tracking
    const prevValueRef = useRef();
    useEffect(() => {
        prevValueRef.current = {
            seoTitleTags: commonData?.seoTitleTags,
            canonicalURL: commonData?.seoTitleTags?.canonicalURL,
            irisCallStatus,
            irisContentCallCompleted,
            pathname: location.pathname,
        };
    });

    // SEO URL update
    useEffect(() => {
        if (__SERVER__ || !mounted) return;

        const { pathname } = location;
        const { seoTitleTags = {} } = commonData;
        const { canonicalURL } = seoTitleTags;
        const prevValue = prevValueRef.current || {};

        if (pathname && prevValue?.seoTitleTags && seoTitleTags) {
            if (
                prevValue?.canonicalURL &&
                !compareUrls(pathname, prevValue?.canonicalURL) &&
                !compareUrls(
                    canonicalUrlPreviewRemove(canonicalURL),
                    canonicalUrlPreviewRemove(prevValue?.canonicalURL)
                )
            ) {
                updateUrl(prevValue?.canonicalURL, {}, true);
            }
        }
    }, [
        location.pathname,
        commonData?.seoTitleTags?.canonicalURL,
        irisCallStatus,
        irisContentCallCompleted,
        mounted,
    ]);

    const canonicalUrlPreviewRemove = useCallback(
        (url = '') => {
            let urlReturn = url;
            if (hostname === 'preview.jcpenney.com') {
                urlReturn = url?.replace(/preview\//i, '');
            }
            return urlReturn;
        },
        [hostname]
    );

    // Memoize iris props
    const irisParams = useMemo(() => getIrisParamForGallery(location), [location]);

    const irisPageProps = useMemo(
        () => ({
            aggregateAPI: true,
            'data-automation-id': pageType,
            irisClientCall: false,
            irisComponents: irisComponentsForGallery,
            irisPageSpecificComponents,
            pageType,
            params: irisParams,
            postProcessor,
            theme: 'gallery',
            zoneRenderer: [
                {
                    zone: 'ABVGRID',
                    renderer: aboveTheGridZoneOverride,
                },
                {
                    renderer: gridZoneRender,
                    zone: 'GRID',
                },
            ],
        }),
        [irisParams, pageType, postProcessor]
    );

    const PageLayout = useCallback(
        (showIrisPage) => (
            <Layout>
                <>
                    {!enableMultiSelectFilters && <GalleryLoader />}
                    <SeoSchema />
                    <StoreAnalytics />
                    <TimeoutError />
                    <ScrollTop showBackToTop={showBackToTop} />
                    {showIrisPage ? (
                        <IrisPage {...irisPageProps} />
                    ) : (
                        <FailOverLayout
                            enableCanonicalFallback={enableCanonicalFallback}
                            location={location}
                            pageType={pageType}
                            seoConfig={seoConfig}
                            hostname={hostname}
                        />
                    )}
                    {enableMiniPDPFeature && <MiniPDPInterstitialAsync />}
                </>
            </Layout>
        ),
        [
            enableMultiSelectFilters,
            showBackToTop,
            irisPageProps,
            enableCanonicalFallback,
            location,
            pageType,
            seoConfig,
            hostname,
            enableMiniPDPFeature,
        ]
    );

    if (irisFallBackStatus || (irisCallStatus === fail && irisContentCallCompleted)) {
        return PageLayout(false);
    }

    return shouldRender ? (
        PageLayout(true)
    ) : (
        <Layout>
            <IrisPage {...irisPageProps} />
        </Layout>
    );
};

IrisWrapper.propTypes = {
    location: object,
    pageType: oneOf(['Gallery', 'Search']).isRequired,
    cookies: shape({ DPCPT: string }),
};

IrisWrapper.defaultProps = {
    location: {},
    cookies: {},
};

export default IrisWrapper;
